import AutoScroll from "embla-carousel-auto-scroll";
import useEmblaCarousel from "embla-carousel-react";
import {Children, useEffect, useMemo, useRef} from "react";
import styles from "./carousel.styles.module.scss";
import {CarouselProps} from "./carousel.types";

const DEFAULT_AUTOSCROLL_SPEED = 1;

const canHoverFinePointer = () =>
  typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;

export const Carousel = ({
  options,
  renderItems,
  children,
  speed = DEFAULT_AUTOSCROLL_SPEED,
  pauseOnHover = true,
  pauseOnDocumentScroll = true,
  loop = true,
  autoScroll: autoScrollEnabled = true,
  onInit,
}) => {
  const pauseHoverOnDesktop = pauseOnHover && canHoverFinePointer();

  const emblaOptions = useMemo(
    () => ({
      loop,
      dragFree: true,
      align: "start",
      startIndex: 0,
      watchSlides: false,
      duration: 20,
      ...options,
    }),
    [options, loop]
  );

  const plugins = useMemo(() => {
    if (!autoScrollEnabled) return [];
    return [
      AutoScroll({
        playOnInit: true,
        stopOnMouseEnter: pauseHoverOnDesktop,
        speed,
        stopOnInteraction: false,
        stopOnFocusIn: false,
        loop,
      }),
    ];
  }, [autoScrollEnabled, speed, pauseHoverOnDesktop, loop]);

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions, plugins);
  const lastInitEmblaRef = useRef(null);
  const prevItemCountRef = useRef(null);

  const items = useMemo(() => {
    if (typeof renderItems === "function") {
      return renderItems();
    }
    if (renderItems) {
      return renderItems;
    }
    return children;
  }, [renderItems, children]);

  const itemCount = Children.toArray(items).length;

  useEffect(() => {
    if (!emblaApi) return;
    const prev = prevItemCountRef.current;
    prevItemCountRef.current = itemCount;
    if (prev === null || prev === itemCount) return;
    emblaApi.reInit();
    emblaApi.scrollTo(0, true);
  }, [emblaApi, itemCount]);

  useEffect(() => {
    if (!emblaApi) {
      lastInitEmblaRef.current = null;
      return;
    }
    if (!onInit) return;
    if (lastInitEmblaRef.current === emblaApi) return;
    lastInitEmblaRef.current = emblaApi;

    const scrollWithPause = scrollFn => {
      const autoScroll = emblaApi.plugins()?.autoScroll;
      if (autoScroll) autoScroll.stop();
      scrollFn();
      if (autoScroll) setTimeout(() => autoScroll.play(), 2000);
    };

    onInit({
      scrollPrev: () => scrollWithPause(() => emblaApi.scrollPrev()),
      scrollNext: () => scrollWithPause(() => emblaApi.scrollNext()),
      scrollTo: (index, jump) => scrollWithPause(() => emblaApi.scrollTo(index, jump)),
    });
  }, [emblaApi, onInit]);

  useEffect(() => {
    if (!emblaApi || !autoScrollEnabled || !pauseOnDocumentScroll) return;
    const autoScroll = emblaApi.plugins()?.autoScroll;
    if (!autoScroll) return;

    const root = emblaApi.rootNode();
    if (!root || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) autoScroll.play();
        else autoScroll.stop();
      },
      {threshold: 0.2}
    );
    observer.observe(root);
    return () => observer.disconnect();
  }, [emblaApi, autoScrollEnabled, pauseOnDocumentScroll]);

  return (
    <div className={styles.carouselWrapper}>
      <div className={styles.leftShadow} />
      <div className={styles.rightShadow} />
      <div className={styles.embla}>
        <div
          className={styles.emblaViewport}
          ref={emblaRef}
        >
          <div className={styles.emblaContainer}>
            {Children.toArray(items).map((child, idx) => (
              <div
                key={child.key || `carousel-slide-${idx}`}
                className={styles.emblaSlide}
              >
                {child}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

Carousel.propTypes = CarouselProps;
